/* ===========================================================================
   ILS — FREE-SHIPPING UPSELL — PRODUCTION — TASK-4841
   ===========================================================================
   The production port of the prototype the shopper approved on the hidden test
   page /pages/delivery-upsell-test (TASK-4835 / 4836 / 4837 / 4838 / 4839 /
   4840). This runs on the REAL delivery page, /<locale>/pages/vybor-dostavki.

   HOW IT IS LOADED, AND HOW TO SWITCH IT OFF
   ------------------------------------------
   Exactly one file loads this one: sections/custom-delivery-upsell.liquid,
   referenced by exactly one template, templates/page.delivery-selector.json.
   That section is gated on
       page.template_suffix == 'delivery-selector' and section.settings.upsell_enabled
   and this file re-checks BOTH off the data island (data-template-suffix,
   data-enabled) before it touches the DOM, injects CSS or issues a request. The
   section setting is the SINGLE kill-switch activation point; the one-step
   disable procedure is documented at the top of that section file.

   WHAT IT DOES NOT TOUCH
   ----------------------
   assets/custom-delivery-selector.{js,css}, sections/custom-delivery-selector
   .liquid, layout/theme.liquid, snippets/ils-weight.liquid and
   assets/custom-add-toast.{js,css} are byte-identical to their pre-task bytes
   (recon/task4841/snapshot/digests.json). The live selector remains solely
   responsible for country / method / operator / pickup-point selection, for its
   own background cart persistence, and for the Next → /checkout redirect.

   -- WHAT WAS DELIBERATELY **NOT** PORTED ----------------------------------
   The prototype's four TEST-ONLY safety behaviours are absent here on purpose;
   each of them would have broken the real page:

     1. TASK-4836's cart-write guard (a fetch / XMLHttpRequest / sendBeacon
        wrapper that suppressed POST /cart/update.js, /cart/change.js and
        /cart/clear.js). On the real page those writes ARE the delivery feature:
        they carry the `delivery_*` cart attributes and `_delivery_*` line-item
        properties to the order. This file installs no wrapper of any kind and
        does not reference those endpoints at all.
     2. The Next interception — a capture-phase click listener that called
        stopImmediatePropagation() on [data-ils-next] so the test page could
        never reach checkout. Gone: Next is entirely the selector's again.
     3. The noindex / nofollow <meta> injection and the hidden-page protections.
     4. Test-page-only routing. The gate is the real page's own suffix.

   Along with them went the transient "checkout is disabled here" note
   (showNote / .ils-ups__note and the `note` copy key in all six locales), which
   existed only to explain the suppressed Next.

   -- THE ONE BEHAVIOUR PRODUCTION NEEDS AND THE TEST PAGE COULD NOT HAVE ----
   stampDeliveryProps(). Because the prototype blocked /cart/change.js it never
   faced the consequence of its own add: a line item created AFTER the selector
   has already persisted the selection carries no `_delivery_*` properties, and
   on this store it is the LINE-ITEM PROPERTIES, not the cart attributes, that
   survive into the final order (TASK-3223 → 3228). The selector will not
   re-stamp the new line by itself: its ensurePersisted() resolves immediately
   while writeState.writtenSig still matches the unchanged selection, so nothing
   re-runs writeLineItemProperties(). See the block comment above
   stampDeliveryProps() for how the gap is closed without inventing a value and
   without touching the selector.

   -- EVERYTHING ELSE IS THE APPROVED BEHAVIOUR, UNCHANGED ------------------
   Ported byte-for-byte from the approved prototype: the block appears only once
   the delivery selection is COMPLETE and stays hidden when the selected method
   is already free; the second line names the SELECTED method only; the gap to
   free shipping is exact to the cent; A-tier then B-tier products each in
   custom.sales_rank order, minus everything already in the cart and minus every
   product with no available variant, capped at twelve cards; the first four
   images eager and the rest lazy behind an IntersectionObserver rooted on the
   scroller; the remaining-gap weight heuristic; the compact horizontal
   scroll-snap carousel with no slider library and no A/B badge; "+ Add" without
   leaving the page; the cart-subtotal row immediately above Back/Next, label on
   the band's left edge and amount on its right, sourced from cart.total_price;
   the selected method's price flipping to €0,00 the moment the threshold is
   crossed, with no page refresh; and the flying thumbnail that lands on the euro
   amount and pulses it.

   -- HOW IT READS THE DELIVERY SELECTION -----------------------------------
   Zero changes to assets/custom-delivery-selector.js. Two existing, already-
   public signals are observed instead:
     • [data-ils-next].disabled — the selector sets this from ONE choke point
       (syncSteps → elNext.disabled = !complete) using its own completeness
       rule: courier ⇒ method chosen; locker ⇒ operator AND pickup point
       chosen. So "Next is enabled" IS "the delivery selection is fully
       complete", by the page's own logic.
     • sessionStorage['ILS_DSEL_STATE_V1'] — the selector's own saved blob
       (TASK-3220/TASK-4833): { manual, country, method, operator, point }.
   A MutationObserver on the selector root re-evaluates on every change, so the
   block appears/disappears/recalculates in the same frame the selection does.

   -- WHY THERE IS NO DELAY, SPINNER OR POST-COMPLETION FETCH ---------------
   Everything the block needs is in hand BEFORE the selection can complete:
     • product data (title, image, variants, prices, availability) is rendered
       INTO the page by Liquid — zero requests, already localised and already
       in the market's currency;
     • the first cards' images are warmed with new Image() at init;
     • the cart is read once at init (/cart.js) and only re-read after the
       shopper adds something;
     • the free-shipping thresholds are prefetched per country the moment a
       country is known (init + every country row tap, capture phase), which is
       always at least one step before the selection can be complete.
   When the block becomes eligible it renders synchronously from memory.

   -- FREE-SHIPPING THRESHOLD SOURCE ---------------------------------------
   The SAME source the selected method's own price comes from: the row returned
   by GET {API_BASE}/api/delivery/rates?country=XX whose `type`/`carrier`
   matches the SELECTED method, field `free_shipping_threshold_eur`. Nothing is
   hardcoded: LV courier 80 / LV locker 35 / DE courier 60 / IE|BE|SE|AT
   courier 100 all come off the wire. If the selected method's row carries no
   threshold, there is no free-shipping offer to upsell toward and the block
   stays hidden.

   -- KNOWN RESIDUALS, INHERITED FROM THE LIVE SELECTOR --------------------
   Two pre-existing behaviours of assets/custom-delivery-selector.js become
   REACHABLE now that the cart can change on this page. Neither is introduced
   here and neither can be fixed without editing that shared file, which
   TASK-4841 does not do:
     • it freezes cart WEIGHT at init (loadCartWeight), so for the 23
       weight-priced countries the courier tariff TIER shown stays the
       load-time one after an add. Below the threshold the displayed courier
       price can therefore understate the tier the cart has grown into. The
       threshold flip to €0,00 is unaffected — that is driven from the live
       cart total here.
     • its Next handler bounces a CONFIRMED-empty cart to /cart, from a count
       also frozen at init. A shopper who lands here with an empty cart, adds
       from this row and presses Next is sent to /cart (where their item is
       waiting) instead of to /checkout. The realistic entry to this page is
       from the cart, so the cart is normally non-empty.
   =========================================================================== */
(function () {
  'use strict';

  /* ---- GATE + KILL SWITCH -------------------------------------------------
     The section that loads this file is already gated in Liquid on BOTH the
     template suffix and section.settings.upsell_enabled; these are the second,
     independent checks of the very same two values, which the section emits
     onto the data island. Before they pass, nothing happens — no DOM, no CSS,
     no fetch, no listener, and the shopper gets exactly the delivery page they
     got before this feature existed.

     PAGE_SUFFIX is the REAL delivery page's template suffix. It is not the page
     HANDLE because this store translates handles (the `delivery-selector` page
     is reached as `vybor-dostavki` on the ru market), and a handle gate would
     silently switch the feature off on translated markets.

     data-enabled is the kill switch's second reading. When the setting is off
     the section emits no data island at all, so this file is never even
     requested; the check is here so that flipping the switch can never depend
     on a cached copy of this asset. */
  var PAGE_SUFFIX = 'delivery-selector';
  var dataEl = document.querySelector('[data-ils-upsell-data]');
  if (!dataEl) return;
  if (dataEl.getAttribute('data-template-suffix') !== PAGE_SUFFIX) return;
  if (dataEl.getAttribute('data-enabled') !== 'true') return;      // KILL SWITCH

  var root = document.querySelector('[data-ils-dsel]');
  var host = root && root.querySelector('.ils-dsel__inner');
  var elNext = root && root.querySelector('[data-ils-next]');
  if (!root || !host || !elNext) return;

  var PAYLOAD;
  try { PAYLOAD = JSON.parse(dataEl.textContent || '{}'); } catch (e) { return; }
  var CANDIDATES = (PAYLOAD && PAYLOAD.products) || [];
  if (!CANDIDATES.length) return;


  /* TASK-4838 — the subtotal row may not paint before this file's stylesheet
     has. The row is created by JS and the stylesheet is fetched by JS, so the
     two race: if /cart.js answers first the row appears for a moment with only
     inherited styles. Measured on the live page, the sheet normally wins by
     ~300ms (applied at 1237ms, row shown at 1552ms on mobile) — but one live
     run caught the other order and rendered the row at 15px/400 in a plain
     block instead of the cart's 13px/500 + 18px/700 flex row. The flash was
     harmless before, when the row was a 12px line; at the cart's proportions it
     is a visible jump, so paintCartTotal() now waits for cssReady. The link's
     own load event is the signal, with error and a 3s timeout as fall-throughs
     so a stylesheet that never arrives can never hide the row permanently. */
  var cssReady = false;
  function markCssReady() {
    if (cssReady) return;
    cssReady = true;
    paintCartTotal();
  }

  /* Stylesheet, injected only now, behind the gate. (The prototype also
     injected <meta name="robots" content="noindex,nofollow"> here; the real
     delivery page is an indexable customer surface, so that is gone.) */
  (function gateSideEffects() {
    var cssHref = dataEl.getAttribute('data-css');
    if (cssHref && !document.querySelector('link[data-ils-upsell-css]')) {
      var l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = cssHref;
      l.setAttribute('data-ils-upsell-css', '');
      l.addEventListener('load', markCssReady);
      l.addEventListener('error', markCssReady);
      document.head.appendChild(l);
      if (l.sheet) markCssReady();
      setTimeout(markCssReady, 3000);
    } else {
      markCssReady();   // nothing to wait for: no href, or it is already there
    }
  })();

  /* ---- "Next" IS NOT TOUCHED ---------------------------------------------
     The prototype registered a capture-phase click listener on `document` that
     called stopImmediatePropagation() on [data-ils-next], so the test page
     could never reach checkout. That listener is deliberately absent here: on
     the real page Next must run the selector's own handler — ensurePersisted()
     then location.href = localeRoot() + 'checkout'. This file adds no listener
     to [data-ils-next], to [data-ils-back], or to the footer at all; the ONLY
     thing it does with elNext is READ its `disabled` property as the
     selection-complete signal. */

  /* =========================================================================
     THE ACTIVE-WEIGHT HEURISTIC — the one place to tune the prototype.
     -------------------------------------------------------------------------
     The task is explicit that ONE product does NOT have to close the whole
     gap, and that the choice must be driven by PRICE, not grams. So:

       target        = remaining_gap * TARGET_SHARE
       chosen variant= the AVAILABLE variant whose price is nearest `target`;
                       on an exact tie the slightly-ABOVE option wins.

     Two guards, both easy to flip:
       BULK_GRAMS          — 3 kg / 5 kg wholesale sizes are excluded from the
                             candidate pool whenever the product has ANY smaller
                             available size, so the block never "reaches the
                             threshold" by proposing a 5 kg sack when a natural
                             consumer size exists. A product that ONLY sells in
                             bulk keeps its bulk sizes.
       MAX_CARD_OVERSHOOT  — a product whose CHEAPEST available variant already
                             costs more than 1.5x the whole remaining gap is
                             dropped from the row (it cannot read as "add a
                             little more"), unless dropping it would leave fewer
                             than MIN_CARDS cards — then the filter is released
                             and the priority order decides.

     NOTE (TASK-4839): MAX_CARDS is a CAP, not a target. It truncates the
     already-ordered list and can never re-order it or pull a product forward,
     so raising it from 8 to 12 only lets more of the SAME sequence through.
     EAGER_IMAGES is a rendering constant, not a selection one — it decides how
     many cards load their image immediately and has no effect on which
     products are chosen.
     ========================================================================= */
  var HEURISTIC = {
    TARGET_SHARE: 0.5,        // aim for ~half the remaining gap per card
    BULK_GRAMS: 3000,         // >= 3 kg counts as a wholesale size
    MAX_CARD_OVERSHOOT: 1.5,  // drop products whose cheapest size > 1.5x the gap
    MIN_CARDS: 3,             // ...unless that would leave fewer than this many
    MAX_CARDS: 12,            // how many cards the carousel may hold (TASK-4839: was 8)
    EAGER_IMAGES: 4           // cards whose image loads at once; the rest are lazy
  };

  /* ---- locale + money ---------------------------------------------------- */
  var LOCALE = (function () {
    try {
      var l = (window.Shopify && window.Shopify.locale) ||
              (document.documentElement && document.documentElement.lang) || 'en';
      return String(l).trim().toLowerCase().split('-')[0] || 'en';
    } catch (e) { return 'en'; }
  })();
  /* TASK-4846 — ONE global money format on EVERY published locale,
     English included: decimal separator ',' and thousands separator '.'
     (€0,00 / €89,90 / €1.234,56). Deliberately not Intl.NumberFormat or
     toLocaleString: this is a storefront business rule, not a locale
     convention, and en-* would render €1,234.56. Same algorithm as
     moneyFromCents() in custom-cart-drawer.js. Presentation only — the
     numeric value and its rounding are untouched. */
  // TASK-4846 supersedes the earlier "English uses a decimal POINT" rule: the
  // separator is now the SAME on all six locales. DEC is kept as a named
  // constant so every call site below reads unchanged.
  var DEC = ',';
  function eurCents(c) {
    /* TASK-5856 — REPORT-5848 D3: the TASK-5795 defect, still live on this file.
       paintCartTotal() feeds cart.total_price straight in, and those are
       PRESENTMENT minor units, so on /en-se/ a 1 100,00 kr cart rendered
       "€1.100,00" in the selector footer — wrong symbol AND a meaningless
       number. This row is not behind the band's hide(), so it stayed visible on
       the six the whole time the band was suppressed. Same three-line
       ILSMoney.fromCents() guard as custom-cart-drawer.js:86-89; the legacy body
       runs whenever the oracle refuses. */
    if (window.ILSMoney && window.ILSMoney.on()) {
      var _p5856 = window.ILSMoney.fromCents(c);
      if (_p5856 !== null) return _p5856;
    }
    var n = Math.round(Number(c) || 0);
    var sign = n < 0 ? '-' : '';
    var abs = Math.abs(n);
    var frac = String(abs % 100);
    while (frac.length < 2) frac = '0' + frac;
    /* TASK-5946 — display only: a FULLY zero fraction prints as no fraction. */
    return sign + '€' + String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (frac === '00' ? '' : DEC + frac);
  }
  // Thresholds are whole euros in every current row (80 / 35 / 60 / 100); keep
  // them clean, and fall back to the 2-decimal form if one ever is not.
  function eurThreshold(n) {
    var v = Number(n);
    var whole = String(Math.round(Math.abs(v))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var sgn = v < 0 ? '-' : '';
    // TASK-4846: grouped even in the whole-euro form (€1.000, not €1000).
    return (Math.round(v * 100) % 100 === 0) ? sgn + '€' + whole : eurCents(Math.round(v * 100));
  }

  /* ---- copy -------------------------------------------------------------
     The APPROVED copy, ported verbatim from the prototype for all six
     currently published storefront locales (en, de, et, lt, lv, ru). Each
     locale carries its own `strong` (the one emphasised word), `cart` (the
     subtotal label), `main`/`mainTail` (the dynamic-gap sentence, split around
     the amount), `courier`/`locker` (the selected-method-only threshold line)
     and `add`. There is no seventh locale here: the task is explicit that no
     speculative future locale is to be added.

     The prototype's `note` key is gone in all six — it was the "checkout is
     disabled here" line, which only the suppressed test-page Next could show.

     Still deliberately NOT routed through the theme's locale files: wiring new
     translation keys through locales/*.json + the per-theme Shopify
     translations would touch shared translation resources this task must not
     disturb, and it would put the copy on a different deployment path from the
     code that renders it. A locale that is not in COPY falls back to en, so a
     future published locale degrades to English rather than to nothing. */
  var COPY = {
    ru: { strong: 'БЕСПЛАТНОЙ', cart: 'Промежуточный итог', main: 'Для БЕСПЛАТНОЙ доставки добавьте в корзину товаров ещё на ',
         courier: 'Бесплатная доставка курьером от ',
         locker:  'Бесплатная доставка в пакомат от ',
         add: '+ Добавить' },
    en: { strong: 'FREE', cart: 'Subtotal', main: 'Add ', mainTail: ' more to your cart for FREE delivery',
         courier: 'Free courier delivery from ',
         locker:  'Free parcel locker delivery from ',
         add: '+ Add' },
    de: { strong: 'KOSTENLOSEN', cart: 'Zwischensumme', main: 'Für KOSTENLOSEN Versand legen Sie noch Artikel für ', mainTail: ' in den Warenkorb',
         courier: 'Kostenlose Kurierlieferung ab ',
         locker:  'Kostenlose Lieferung an den Paketautomaten ab ',
         add: '+ Hinzufügen' },
    lv: { strong: 'BEZMAKSAS', cart: 'Starpsumma', main: 'Lai saņemtu BEZMAKSAS piegādi, pievieno grozam preces vēl par ',
         courier: 'Bezmaksas kurjerpiegāde no ',
         locker:  'Bezmaksas piegāde uz pakomātu no ',
         add: '+ Pievienot' },
    lt: { strong: 'NEMOKAMAM', cart: 'Tarpinė suma', main: 'NEMOKAMAM pristatymui įdėkite į krepšelį prekių dar už ',
         courier: 'Nemokamas kurjerio pristatymas nuo ',
         locker:  'Nemokamas pristatymas į paštomatą nuo ',
         add: '+ Pridėti' },
    et: { strong: 'TASUTA', cart: 'Vahesumma', main: 'TASUTA tarne saamiseks lisa ostukorvi kaupa veel ', mainTail: ' eest',
         courier: 'Tasuta kullertarne alates ',
         locker:  'Tasuta tarne pakiautomaati alates ',
         add: '+ Lisa' }
  };
  var T = COPY[LOCALE] || COPY.en;

  /* ---- TASK-4837: ONE emphasised word, the rest of the sentence normal ----
     The primary line used to be bold end to end (.ils-ups__main carried
     font-weight: 700). The approved design keeps only the "free" word bold —
     "БЕСПЛАТНОЙ" on ru — with the rest of the sentence at the page's normal
     weight and the dynamic amount still carrying the red accent.

     The copy is NOT re-authored to achieve that. This helper re-splits the
     SAME string around the first literal occurrence of T.strong, so the text
     the shopper reads is character-for-character what it was before. A locale
     with no `strong`, or one whose word is not present in the string, gets a
     single plain text node — the sentence can never lose a character.

     emphasisedYet spans BOTH halves of the line, because en / de / et carry
     the word in mainTail (after the amount), not in main. */
  var emphasisedYet = false;
  function appendCopy(parent, text) {
    var s = String(text || '');
    if (!s) return;
    var w = T.strong;
    var at = (!emphasisedYet && w) ? s.indexOf(w) : -1;
    if (at < 0) { parent.appendChild(document.createTextNode(s)); return; }
    emphasisedYet = true;
    if (at > 0) parent.appendChild(document.createTextNode(s.slice(0, at)));
    var b = document.createElement('span');
    b.className = 'ils-ups__free';
    b.textContent = w;
    parent.appendChild(b);
    var tail = s.slice(at + w.length);
    if (tail) parent.appendChild(document.createTextNode(tail));
  }

  /* ---- storefront helpers ------------------------------------------------ */
  function localeRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }
  // Same host the delivery selector itself talks to (API_BASE in
  // assets/custom-delivery-selector.js). No new third-party service.
  var API_BASE = 'https://alert-dedication-production.up.railway.app';

  /* ---- cart (browser session only) --------------------------------------- */
  var cartCents = null;          // cart.total_price — the exact field the selector compares
  var cartProductIds = {};       // product_id -> true, for the "already in cart" exclusion
  var cartToken = '';            // cheap change-detector for the render signature
  // TASK-4916 — the REAL Shopify cart token, which the line above is NOT (that
  // one is a locally built render signature). This is the bare id from
  // /cart.js `token` with the "?key=…" suffix stripped, and it comes back on
  // the resulting order as `order.cart_token` — so carrying it on this block's
  // two analytics events is what lets a later report answer "did a cart that
  // added from the delivery upsell go on to purchase?". Never rendered, never
  // written to a cart attribute or a line-item property.
  var shopCartToken = null;
  // TASK-4838: cart.total_weight in kg, the same field/units the live selector
  // reads for its weight-priced courier tariff (loadCartWeight, TASK-3210).
  // Only ever used as a FALLBACK when restoring a paid price the selector
  // itself never rendered — see paidCourierPrice().
  var cartWeightKg = null;

  function readCart() {
    return fetch(localeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c) return;
        cartCents = typeof c.total_price === 'number' ? c.total_price : cartCents;
        if (typeof c.total_weight === 'number') cartWeightKg = c.total_weight / 1000;   // TASK-4838
        if (c.token) shopCartToken = String(c.token).split('?')[0] || null;                // TASK-4916
        cartProductIds = {};
        (c.items || []).forEach(function (it) { if (it.product_id) cartProductIds[String(it.product_id)] = true; });
        cartToken = String(cartCents) + '|' + Object.keys(cartProductIds).sort().join(',');
        paintCartTotal();      // TASK-4837 — the footer total follows the same read
        syncDeliveryPrices();  // TASK-4838 — ...and so does the delivery price
      })
      .catch(function () { /* leave the previous values; the block just stays hidden */ });
  }

  /* =========================================================================
     TASK-4838 — the CART PAGE's "Промежуточный итог" row, above Back/Next
     -------------------------------------------------------------------------
     WHERE. The row is inserted as the FIRST CHILD of the live selector's own
     .ils-dsel__footer, above .ils-dsel__footer-row, so it sits immediately
     above "Назад" / "Далее" and stays visible at any scroll position. That
     insertion is runtime DOM — not one byte of
     sections/custom-delivery-selector.liquid or of
     assets/custom-delivery-selector.{js,css} changes, and this file is loaded
     exclusively by sections/custom-delivery-upsell.liquid, behind its
     template-suffix + kill-switch gate.

     WHAT. TASK-4837 rendered a compact "В корзине: €XX,XX". TASK-4838 replaces
     that with the shop's OWN cart subtotal row, so the prototype's footer reads
     like the cart the shopper just came from:

         <div class="subt">
           <span class="l">Промежуточный итог</span>
           <span class="v"><span class="tlab-currency-format">€7,98</span></span>
         </div>

     — the live markup of .ils-cp__foot .subt on /ru-eu/cart (captured in
     recon/task4838/cart-dom-probe.json). The label text is that row's exact RU
     string; the structure here keeps this page's own class names
     (.ils-ups__cart / -label / -amount) so no shared cart rule is borrowed and
     nothing on this page can perturb the real cart. The visual proportions are
     MEASURED from that row rather than copied out of its stylesheet — label
     13px / 500 / muted in a right-aligned 60% column, amount 18px / 700 / ink
     — because on this store custom-typo-3364-full.css overrides
     custom-cart-drawer.css with !important (600 / 17px / 800 would have been
     the wrong answer). See the CSS file for the numbers and their source.

     THE NUMBER. cart.total_price, in cents — the SAME field, from the SAME
     /cart.js read, that the free-shipping gap is computed from
     (missing = threshold * 100 - cartCents), and the same field the live cart
     row shows (probe: .subt .v read "€7,98" while /cart.js reported 798). The
     two lines therefore cannot disagree, and both follow discounts exactly as
     the cart itself reports them. It is formatted by this page's own
     eurCents(), i.e. always two decimals with the locale's separator:
     "€12,34" on every published locale, English included (TASK-4846).

     HEIGHT. A taller footer would otherwise hide the bottom of the page: the
     selector reserves room with padding-bottom: calc(var(--dsel-foot-h) + 16px)
     on .ils-dsel__inner. syncFootHeight() MEASURES the footer and writes the
     real height back into that variable as an inline custom property on the
     selector root, so the reservation also covers env(safe-area-inset-bottom)
     on a real phone. (The selector's point-list autoscroll already measures the
     footer's live rect instead of this variable, so it needs nothing.)
     ========================================================================= */
  var cartRow = null, cartAmountEl = null, cartBumpTimer = null;
  var lastFootH = null, lastCartShown = null;
  // TASK-4838 — the red confirmation pulse is OWED to the flying thumbnail:
  // the number moves as soon as /cart.js answers (~200ms), the flight lands at
  // 800ms, and the task asks for the pulse to follow the landing.
  var flightsInAir = 0, pulseOwed = false;

  function buildCartRow() {
    if (cartRow) return;
    var footer = root.querySelector('.ils-dsel__footer');
    if (!footer) return;
    var footRow = footer.querySelector('.ils-dsel__footer-row');
    cartRow = document.createElement('div');
    cartRow.className = 'ils-ups__cart';
    cartRow.setAttribute('data-ils-upsell', '');
    cartRow.setAttribute('role', 'status');
    cartRow.hidden = true;               // until the first /cart.js read lands
    var label = document.createElement('span');
    label.className = 'ils-ups__cart-label';
    label.textContent = T.cart || COPY.en.cart;   // TASK-4838: "Промежуточный итог"
    cartAmountEl = document.createElement('span');
    cartAmountEl.className = 'ils-ups__cart-amount';
    cartRow.appendChild(label);
    cartRow.appendChild(cartAmountEl);
    if (footRow) footer.insertBefore(cartRow, footRow);
    else footer.appendChild(cartRow);
    paintCartTotal();
  }

  function syncFootHeight() {
    var footer = root.querySelector('.ils-dsel__footer');
    if (!footer) return;
    var h = Math.ceil(footer.getBoundingClientRect().height);
    if (!h || h === lastFootH) return;
    lastFootH = h;
    root.style.setProperty('--dsel-foot-h', h + 'px');
  }

  function paintCartTotal() {
    if (!cartRow) return;
    // cssReady: see markCssReady() — never show the row before its stylesheet.
    if (cartCents == null || !cssReady) { cartRow.hidden = true; nextFrame(syncFootHeight); return; }
    cartRow.hidden = false;
    var txt = eurCents(cartCents);
    if (txt !== lastCartShown) {
      // The number itself moves the instant the cart read lands — never delayed
      // by the animation. Only the PULSE waits for the thumbnail.
      cartAmountEl.textContent = txt;
      // Subtle confirmation that the number just MOVED — skipped on the very
      // first paint, when there is no previous value to have moved from.
      if (lastCartShown !== null) requestPulse();
      lastCartShown = txt;
    }
    nextFrame(syncFootHeight);
  }

  /* TASK-4838 — the pulse, and the handshake that holds it until landing.
     requestPulse() fires immediately when nothing is in the air (reduced
     motion, or a cart change with no add behind it); otherwise it is owed and
     flightLanded() pays it. If the cart read is SLOWER than the 800ms flight
     the flight lands owing nothing and requestPulse() runs on its own — so the
     pulse happens exactly once either way. */
  function pulseAmount() {
    if (!cartRow) return;
    cartRow.classList.remove('is-bumped');
    void cartRow.offsetWidth;            // restart the transition
    cartRow.classList.add('is-bumped');
    if (cartBumpTimer) clearTimeout(cartBumpTimer);
    cartBumpTimer = setTimeout(function () { cartRow.classList.remove('is-bumped'); }, 260);
  }
  function requestPulse() {
    if (flightsInAir > 0) { pulseOwed = true; return; }
    pulseAmount();
  }
  function flightLanded() {
    if (flightsInAir > 0) flightsInAir--;
    if (flightsInAir === 0 && pulseOwed) { pulseOwed = false; pulseAmount(); }
  }

  /* =========================================================================
     TASK-4837 / 4838 — the "flies to the cart subtotal" add confirmation
     -------------------------------------------------------------------------
     MIRRORED, not borrowed by reference, from the storefront's own add-to-cart
     animation (flyToCart in assets/custom-add-toast.js, TASK-1761): a 48px
     circular thumbnail of the added product, dropped at the button's centre and
     arced toward the cart with the SAME timing and the SAME visual spec —
     800ms ease-in-out; scale 1 -> 0.75 -> 0.3; rotate 0 -> 140 -> 260deg;
     opacity 1 -> 0.95 -> 0; mid-point lifted by clamp(40, |dx| * 0.3, 120)px so
     the path arcs instead of sliding.

     TASK-4838 — WHERE IT LANDS, AND WHY THE KEYFRAMES BECAME A rAF LOOP.
     TASK-4837 aimed at the centre of the whole ROW and handed three fixed WAAPI
     keyframes to the compositor. Both had to change:

       • the destination is now .ils-ups__cart-amount — the element that holds
         the EURO AMOUNT — not .ils-ups__cart. Its visual centre is the target.
       • that element MOVES while the thumbnail is in the air. The flight starts
         the instant /cart/add.js succeeds; /cart.js answers ~200ms later and
         the number is repainted mid-flight, and because the row is
         space-between with the amount hugging the right edge, one more digit
         (€9,98 -> €19,97) walks its centre several px to the LEFT. A WAAPI
         keyframe list is fixed at creation, so it would have landed beside the
         number it was aiming at.

     So the arc is driven by requestAnimationFrame and the destination is
     re-read from cartAmountEl.getBoundingClientRect() on EVERY frame, including
     the last — the thumbnail therefore ends exactly on the amount's current
     visual centre by construction, not by a coordinate captured 800ms earlier.
     The curve is unchanged: a quadratic Bezier whose control point is
     (dx/2, dy/2 - 2*arcLift) passes through exactly the (dx/2, dy/2 - arcLift)
     mid-point the mirrored keyframes did at offset 0.55, the scale/rotation/
     opacity tracks are the same piecewise ramps broken at 0.55, and the timing
     function is the same ease-in-out (the CSS cubic-bezier(.42,0,.58,1), solved
     per frame). rAF is universal, so the element.animate fallback branch is
     gone rather than replaced. The 1.1s safety cleanup stays.

     WHY IT IS MIRRORED RATHER THAN REUSED IN PLACE. The live effect cannot be
     invoked from outside: flyToCart() is private to its IIFE and is reached
     only through that file's own fetch wrapper, for a button matched by its
     ADD_BUTTON_SELECTOR. Two facts make in-place reuse impossible WITHOUT
     editing shared files:
       • its destination is the header cart icon (#cart-icon-bubble / .ils-cart).
         The delivery page hides the whole theme chrome, so that element is
         absent or 0x0 and the live code takes its own early return — which is
         why the test page gets no flight at all today. Re-aiming it at the new
         total would mean editing assets/custom-add-toast.js, which this task
         forbids.
       • the one supported hook, its [data-upsell-add] selector, would hand the
         button to exactly that header-icon path, not to our row.
     Putting the attribute on the button and monkey-patching the private
     function is the "risky monkey-patching" the task rules out. Mirroring the
     keyframes is smaller, carries no dependency on a shared file, and cannot
     perturb the live effect on the rest of the shop. The class names are this
     page's own (.ils-ups__fly), so the shared .ils-fly rules are not borrowed
     either — nothing here breaks if that stylesheet later changes.

     IT CANNOT GET IN THE WAY. The element is position:fixed,
     pointer-events:none, 48px, aria-hidden, and appended to <body> — outside
     the selector root, so it cannot even trip this block's own
     MutationObserver. It is removed on finish and again on a safety timer, and
     nothing waits on it: the cart re-read and the total repaint run on their
     own schedule while it is still in the air. prefers-reduced-motion: reduce
     skips it entirely, exactly as the live .ils-fly rule does.
     ========================================================================= */
  function motionOK() {
    try { return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }
    catch (e) { return true; }
  }

  // CSS ease-in-out = cubic-bezier(.42, 0, .58, 1). Solved by bisection on x(t),
  // 18 halvings (< 4e-6), which is far finer than a 60fps frame can show. Same
  // curve the WAAPI easing option 'ease-in-out' applied before.
  function easeInOut(x) {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    var P1 = 0.42, P2 = 0.58;
    var lo = 0, hi = 1, t = x, i, u, bx;
    for (i = 0; i < 18; i++) {
      t = (lo + hi) / 2;
      u = 1 - t;
      bx = 3 * u * u * t * P1 + 3 * u * t * t * P2 + t * t * t;
      if (bx < x) lo = t; else hi = t;
    }
    t = (lo + hi) / 2; u = 1 - t;
    // y-track of cubic-bezier(.42, 0, .58, 1): y1 = 0, y2 = 1.
    return 3 * u * t * t * 1 + t * t * t;
  }

  var FLY_MS = 800, FLY_BREAK = 0.55;

  // TASK-4838: the destination element is the AMOUNT. Returns its live visual
  // centre, or null when it cannot be a target (missing / hidden / zero-sized).
  function amountCentre() {
    if (!cartRow || cartRow.hidden || !cartAmountEl) return null;
    var r = cartAmountEl.getBoundingClientRect();
    if (!r.width && !r.height) return null;
    return { x: r.left + r.width / 2, y: r.top + r.height / 2, rect: r };
  }

  function flyToCartTotal(from, imgSrc) {
    if (!from || !document.body) { flightLanded(); return; }
    if (!motionOK()) { flightLanded(); return; }
    var to = amountCentre();
    if (!to || (!from.width && !from.height)) { flightLanded(); return; }

    var startX = from.left + from.width / 2;
    var startY = from.top + from.height / 2;

    var fly = document.createElement('div');
    fly.className = 'ils-ups__fly';
    fly.setAttribute('data-ils-upsell', '');
    fly.setAttribute('aria-hidden', 'true');
    if (imgSrc) {
      var img = document.createElement('img');
      img.className = 'ils-ups__fly-img';
      img.alt = '';
      img.src = imgSrc;
      fly.appendChild(img);
    } else {
      // Same fallback mark the live effect uses when it cannot find a thumbnail.
      fly.className = 'ils-ups__fly ils-ups__fly--fallback';
      fly.innerHTML =
        '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
          '<path d="M3.5 8.5 L6.8 11.6 L12.8 5.2" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
    }
    fly.style.left = Math.round(startX) + 'px';
    fly.style.top = Math.round(startY) + 'px';
    fly.style.transform = 'translate(-50%, -50%) translate(0px, 0px) scale(1) rotate(0deg)';
    fly.style.opacity = '1';
    document.body.appendChild(fly);

    flightsInAir++;
    var gone = false, t0 = null, last = null;
    function cleanup() {
      if (gone) return;
      gone = true;
      if (fly.parentNode) fly.parentNode.removeChild(fly);
      // Record where it actually finished, for the verifier and the console.
      try { window.__ILS_UPSELL_FLY = { landedAt: last, target: amountCentre(), at: Date.now() }; } catch (e) {}
      flightLanded();
    }

    function step(ts) {
      if (gone) return;
      if (t0 == null) t0 = ts;
      var p = (ts - t0) / FLY_MS;
      if (p > 1) p = 1;
      var e = easeInOut(p);

      // THE re-aim: the amount's centre, THIS frame. If the row vanished
      // mid-flight we keep the last known target rather than snapping to 0,0.
      var c = amountCentre();
      if (c) last = { x: c.x, y: c.y };
      var tx = last ? last.x : startX, ty = last ? last.y : startY;
      var dx = tx - startX, dy = ty - startY;

      // Quadratic Bezier through the mirrored keyframes' own mid-point.
      var arcLift = Math.min(120, Math.max(40, Math.abs(dx) * 0.3));
      var cx = dx / 2, cy = dy / 2 - 2 * arcLift;
      var u = 1 - e;
      var x = 2 * u * e * cx + e * e * dx;
      var y = 2 * u * e * cy + e * e * dy;

      // Same piecewise tracks as the keyframes, broken at offset 0.55.
      var k = e <= FLY_BREAK ? e / FLY_BREAK : (e - FLY_BREAK) / (1 - FLY_BREAK);
      var scale = e <= FLY_BREAK ? 1 + (0.75 - 1) * k : 0.75 + (0.3 - 0.75) * k;
      var rot = e <= FLY_BREAK ? 140 * k : 140 + 120 * k;
      var op = e <= FLY_BREAK ? 1 - 0.05 * k : 0.95 * (1 - k);

      fly.style.transform = 'translate(-50%, -50%) translate(' + x + 'px, ' + y + 'px) scale(' + scale + ') rotate(' + rot + 'deg)';
      fly.style.opacity = String(op);

      if (p >= 1) { cleanup(); return; }
      nextFrame(step);
    }
    nextFrame(step);
    // Same safety net as the live effect: a backgrounded tab stops rAF, so the
    // node can never outlive the interaction.
    setTimeout(function () { if (fly.parentNode) cleanup(); }, FLY_MS + 300);
  }

  /* ---- rates / thresholds ------------------------------------------------
     TASK-4838: the SAME rows now also yield the PAID price (price_eur) and, for
     the weight-priced countries, the same /api/delivery/weight-rates curve the
     live selector uses. Nothing new is invented and no rate is hardcoded — this
     is the identical request the selector's own loadRates() makes, one field
     wider, so that a price this page blanked to €0,00 can be restored from live
     data if the cart ever drops back below the threshold. The RESTORE still
     prefers the exact string the selector itself rendered (see applyRowPrice);
     this is only the fallback for a row that was already free when the page
     loaded and therefore never showed its paid price. */
  var rates = {};   // iso(lower) -> { loading:Promise } | { data:{courier, operators, tiers} }

  function prefetchRates(iso) {
    iso = String(iso || '').trim().toLowerCase();
    if (!iso || rates[iso]) return;
    var uc = encodeURIComponent(iso.toUpperCase());
    var p = Promise.all([
      fetch(API_BASE + '/api/delivery/rates?country=' + uc, { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); }),
      // Optional leg, exactly as in the selector: DE/LV/LT/EE return an empty
      // curve and fall back to the flat /rates tariff.
      fetch(API_BASE + '/api/delivery/weight-rates?country=' + uc, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
    ])
      .then(function (res) {
        var d = res[0], wr = res[1];
        var parsed = { courier: null, operators: {}, tiers: weightTiers(wr && wr.rates) };
        (d && d.rates ? d.rates : []).forEach(function (row) {
          if (row.type === 'courier') parsed.courier = { threshold: row.free_shipping_threshold_eur, price: row.price_eur };
          else if (row.type === 'locker') parsed.operators[row.carrier] = { threshold: row.free_shipping_threshold_eur, price: row.price_eur };
        });
        rates[iso] = { data: parsed };
        schedule();
      })
      .catch(function () { delete rates[iso]; });   // allow a later retry
    rates[iso] = { loading: p };
  }

  // The selector's own two helpers (weightTiers / priceForWeight, TASK-3210),
  // mirrored verbatim so a restored courier price is computed the way the live
  // page computes it. `weight_kg` is the tier's upper bound; the null "<1"
  // bucket becomes an ~0.99 kg tier.
  function weightTiers(rws) {
    if (!rws || !rws.length) return [];
    return rws.map(function (r) {
      return { to: (r.weight_kg == null ? 0.99 : Number(r.weight_kg)), price: Number(r.client_price) };
    }).filter(function (t) { return !isNaN(t.to) && !isNaN(t.price); })
      .sort(function (a, b) { return a.to - b.to; });
  }
  function priceForWeight(tiers, kg) {
    for (var i = 0; i < tiers.length; i++) { if (kg <= tiers[i].to) return tiers[i].price; }
    return tiers.length ? tiers[tiers.length - 1].price : null;
  }

  /* =========================================================================
     TASK-4838 — the SELECTED delivery price, reactive with no page refresh
     -------------------------------------------------------------------------
     THE BUG. assets/custom-delivery-selector.js reads /cart.js exactly once, in
     loadCartWeight() at init, and keeps the answer in cartSubtotalEur. Its
     free-shipping override — isFreeShip(threshold) / displayShipPrice(price,
     threshold), TASK-3539 — is then evaluated only while buildMethods() /
     buildOperators() render a row. Without the sync below a shopper could add
     enough from the upsell row to cross the threshold, watch the upsell block
     correctly disappear, and still see the OLD paid delivery price until they
     reloaded the page.

     THE FIX, WITHOUT TOUCHING THE SELECTOR. The selector already put the answer
     in the DOM; only its INPUT went stale. So after every cart read this page
     re-applies the selector's own rule to the rows the selector rendered:

         free  <=>  threshold != null && cart.total_price / 100 >= threshold

     ...the identical predicate, against the identical per-country, per-method
     free_shipping_threshold_eur from GET /api/delivery/rates that this page
     already fetches for the upsell copy. The zero is written with the
     selector's own format — eur(0) === "€0,00" — so the glyph and the shape of
     the number do not change, only its value. (TASK-4846 brought the selector's
     eur() and this page's eurCents() onto ONE format for every locale; eurSel()
     below is kept as the explicit mirror, and an offline test asserts the two
     agree, so they cannot drift apart again unnoticed.)

     WHAT IT TOUCHES. Only .ils-dsel__opt-price — the two, and only two, places
     the selector prints a shipping price (custom-delivery-selector.js:669 for
     the courier method card, :719 for each locker operator row). Every row of
     the CURRENT country is kept consistent, which is exactly the state a reload
     would produce; the SELECTED method and the SELECTED operator are covered by
     that. Nothing else in the selector is read, written, re-rendered or reset:
     no selection is cleared, no list re-opens, no scroll moves, and no cart or
     checkout write happens from here — this function only rewrites the TEXT of
     a price element the selector already rendered.

     GOING BACK DOWN. A row this page blanked remembers what the selector had
     printed, in data-ils-paid on the element itself; if the cart falls back
     below the threshold the exact original string is put back. A row that was
     ALREADY free when the page loaded never showed a paid price, so there is
     nothing to remember — that one falls back to the live /rates price, through
     the same weight-tier curve the selector uses for the 23 weight-priced
     countries. Either way the number comes from live data, never from a stale
     render.

     NO FEEDBACK LOOP. Each price element this page writes is tagged
     data-ils-upsell, which is precisely what ownNode() in the change
     detector below already treats as "mine" — so the MutationObserver ignores
     these writes instead of re-entering render(). The selector never edits a
     price node in place (both call sites rebuild their container from
     innerHTML = ''), so the tag can never mask a real selector update: a
     rebuild replaces the node, the fresh one is untagged, and the next sync
     pass re-applies to it.
     ========================================================================= */
  // TASK-4846: the selector's eur() now emits the global format, so its mirror
  // here moves with it — these two MUST stay byte-identical to
  // custom-delivery-selector.js eur(), or applyRowPrice() below would keep
  // rewriting a row that already reads correctly.
  /* TASK-5856 — these two mirror custom-delivery-selector.js so applyRowPrice()
     does not keep rewriting a row that already reads correctly. The selector's
     eur() now routes through ILSDeliveryCore.money(), and its eur(0) is
     therefore the market's own zero ("0,00 kr"), so FREE_TXT has to be computed
     the same way instead of being the hardcoded '€0,00' it was. Both fall back
     to the previous literals on EUR markets and whenever the oracle refuses. */
  function eurSel(n) {
    var v = Number(n);
    if (!isFinite(v)) v = 0;
    var C = window.ILSDeliveryCore;
    if (C && typeof C.money === 'function') {
      try {
        var s = C.money(v);
        if (typeof s === 'string' && s.length && s !== '—') return s;
      } catch (e) {}
    }
    return eurCents(Math.round(v * 100));
  }
  function freeTxt() { return eurSel(0); }
  var FREE_TXT = '€0,00';                                   // EUR fallback only

  function paidPriceFor(kind, slot, row) {
    if (kind === 'courier' && slot.tiers && slot.tiers.length) {
      var p = priceForWeight(slot.tiers, cartWeightKg == null ? 0 : cartWeightKg);
      if (p != null) return p;
    }
    return row.price;
  }

  function applyRowPrice(el, kind, slot, row) {
    if (!el || !row || row.threshold == null || cartCents == null) return;
    /* TASK-5795 — cartCents is PRESENTMENT minor units, row.threshold is the
       EUR free_shipping_threshold_eur: the live defect, reproduced on /en-se/
       where a 150,00 kr cart blanked the courier price to "€0,00". Mirrors
       isFreeShip() in custom-delivery-selector.js, including the containment:
       on a local-currency market no row is ever blanked, because the native
       trigger is not computable here (TASK-5784 §1.3) and Checkout provably
       charges at the advertised amount (TASK-5793). */
    if (window.ILSMoney && window.ILSMoney.on() && !window.ILSMoney.mayClaimFreeShipping()) return;
    var _eur5795 = (window.ILSMoney && window.ILSMoney.on())
      ? window.ILSMoney.eurFromCents(cartCents)
      : (cartCents / 100);
    if (_eur5795 === null) return;
    /* TASK-5856 — mirrors isFreeShip() in custom-delivery-selector.js: on a
       fixed-local market the trigger is the APPROVED local amount from the
       matrix, not row.threshold (null for all six since TASK-5830). */
    var _thr5856 = Number(row.threshold);
    var _R5856 = window.ILSRewardMatrix;
    if (_R5856 && typeof _R5856.freeShipLocalEur === 'function' && _R5856.isFixedLocal()) {
      var _fl5856 = _R5856.freeShipLocalEur();
      if (_fl5856 === null) return;
      _thr5856 = _fl5856;
    }
    if (!isFinite(_thr5856) || _thr5856 <= 0) return;
    var free = _eur5795 >= _thr5856;
    var cur = (el.textContent || '').trim();
    var freeLabel = freeTxt();
    if (!el.hasAttribute('data-ils-upsell')) el.setAttribute('data-ils-upsell', '');
    if (free) {
      if (cur !== freeLabel) {
        if (!el.hasAttribute('data-ils-paid')) el.setAttribute('data-ils-paid', cur);
        el.textContent = freeLabel;
      }
      return;
    }
    // Not free (any more).
    if (el.hasAttribute('data-ils-paid')) {
      var paid = el.getAttribute('data-ils-paid');
      el.removeAttribute('data-ils-paid');
      if (paid && cur !== paid) el.textContent = paid;
      return;
    }
    // Never blanked by us, yet it reads €0,00 — the selector rendered it free
    // from a cart that has since shrunk. Restore from the live rate.
    if (cur === FREE_TXT) {
      var live = paidPriceFor(kind, slot, row);
      if (live != null && Number(live) > 0) el.textContent = eurSel(live);
    }
  }

  function syncDeliveryPrices() {
    if (cartCents == null) return;
    var b = blob();
    if (!b || !b.country) return;
    var s = rates[String(b.country).toLowerCase()];
    if (!s || !s.data) return;
    var slot = s.data;

    var courierEl = root.querySelector('[data-ils-methods] .ils-dsel__opt[data-value="courier"] .ils-dsel__opt-price');
    if (courierEl && slot.courier) applyRowPrice(courierEl, 'courier', slot, slot.courier);

    var rows = root.querySelectorAll('[data-ils-operators] .ils-dsel__opt');
    for (var i = 0; i < rows.length; i++) {
      var id = rows[i].getAttribute('data-value');
      var op = id && slot.operators[id];
      if (op) applyRowPrice(rows[i].querySelector('.ils-dsel__opt-price'), 'locker', slot, op);
    }
  }

  /* ---- the selector's own saved selection -------------------------------- */
  function blob() {
    try { return JSON.parse(sessionStorage.getItem('ILS_DSEL_STATE_V1') || 'null'); }
    catch (e) { return null; }
  }

  // The threshold of the method the shopper ACTUALLY selected — courier row, or
  // the row of the specific locker operator. Never the other method's.
  function selectedThreshold(b) {
    if (!b || !b.country) return null;
    var slot = rates[String(b.country).toLowerCase()];
    if (!slot || !slot.data) return null;
    if (b.method === 'courier') return slot.data.courier ? slot.data.courier.threshold : null;
    if (b.method === 'locker') {
      var op = slot.data.operators[b.operator];
      return op ? op.threshold : null;
    }
    return null;
  }

  /* ---- variant helpers --------------------------------------------------- */
  // Grams from the label the store's own formatter produced ("125 г", "1 кг",
  // "1,5 kg", "500 g") — so the bulk guard uses exactly what the shopper sees.
  function gramsOf(v) {
    var s = String(v.label || '');
    var m = s.replace(/\s+/g, '').replace(',', '.').match(/[\d.]+/);
    if (!m) return null;
    var n = parseFloat(m[0]);
    if (isNaN(n)) return null;
    return /кг|kg/i.test(s) ? Math.round(n * 1000) : Math.round(n);
  }

  // THE heuristic, in one function. `missingCents` is the remaining gap.
  function pickVariant(product, missingCents) {
    var avail = product.variants.filter(function (v) { return v.available; });
    if (!avail.length) return null;
    var nonBulk = avail.filter(function (v) {
      var g = gramsOf(v);
      return g == null || g < HEURISTIC.BULK_GRAMS;
    });
    var pool = nonBulk.length ? nonBulk : avail;
    var target = missingCents * HEURISTIC.TARGET_SHARE;
    var best = null, bestD = Infinity;
    pool.forEach(function (v) {
      var d = Math.abs(v.price - target);
      // strictly-nearer wins; on an exact tie the slightly-ABOVE option wins
      if (d < bestD || (d === bestD && best && v.price > best.price)) { best = v; bestD = d; }
    });
    return best;
  }

  function cheapestAvailable(product) {
    var c = null;
    product.variants.forEach(function (v) { if (v.available && (!c || v.price < c.price)) c = v; });
    return c;
  }

  // Priority order is preserved as authored (A-tier by sales rank, then B-tier);
  // the filters only remove, they never re-sort.
  function chooseProducts(missingCents) {
    var inStock = CANDIDATES.filter(function (p) {
      if (cartProductIds[String(p.id)]) return false;          // already in the cart
      return p.variants.some(function (v) { return v.available; });
    });
    var sensible = inStock.filter(function (p) {
      var c = cheapestAvailable(p);
      return c && c.price <= missingCents * HEURISTIC.MAX_CARD_OVERSHOOT;
    });
    var list = sensible.length >= HEURISTIC.MIN_CARDS ? sensible : inStock;
    return list.slice(0, HEURISTIC.MAX_CARDS);
  }

  /* ---- DOM --------------------------------------------------------------- */
  var block = null, elMain = null, elSub = null, elScroller = null;
  var builtFor = '';        // signature of the card SET currently in the DOM
  var manualPick = {};      // handle -> variant id chosen by tapping a weight chip
  var lastSig = null;

  function buildShell() {
    block = document.createElement('div');
    block.className = 'ils-ups';
    block.setAttribute('data-ils-upsell', '');
    block.setAttribute('data-prototype', 'TASK-4835 TEST PAGE ONLY');
    block.innerHTML =
      '<p class="ils-ups__main"></p>' +
      '<p class="ils-ups__sub"></p>' +
      '<div class="ils-ups__viewport"><div class="ils-ups__scroller"></div></div>';
    elMain = block.querySelector('.ils-ups__main');
    elSub = block.querySelector('.ils-ups__sub');
    elScroller = block.querySelector('.ils-ups__scroller');
    // Appended to the selector's own inner column: BELOW every delivery step,
    // ABOVE the fixed Back/Next bar (which lives outside .ils-dsel__inner).
    host.appendChild(block);
  }

  function cardFor(product, index) {
    var card = document.createElement('div');
    card.className = 'ils-ups__card';
    // NOTE: p.handle is the LOCALISED handle on a translated market (ru renders
    // "sushenyi-mango-bez-sakhara-vetnam" for dried-mango-...-vietnam), so the
    // stable identity used for the cart exclusion is the product id.
    card.setAttribute('data-handle', product.handle);
    card.setAttribute('data-pid', String(product.id));
    var imgbox = document.createElement('div');
    imgbox.className = 'ils-ups__imgbox';
    if (product.image) {
      // TASK-4839 — twelve cards means twelve images, and all but the first two
      // or three are off-screen until the shopper swipes. `loading` and
      // `fetchpriority` are set BEFORE `src`: an <img> that already has a src
      // when `loading="lazy"` is assigned has started loading and the hint is
      // ignored. For the lazy ones the URL is not even in `src` yet — it waits
      // in data-src until observeLazyImages() promotes it, so the tail of the
      // row costs nothing at all on first render.
      var eager = !(index >= HEURISTIC.EAGER_IMAGES);
      var img = document.createElement('img');
      img.className = 'ils-ups__img';
      img.alt = '';
      img.loading = eager ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.setAttribute('fetchpriority', eager ? 'auto' : 'low');
      if (eager) img.src = product.image;
      else img.setAttribute('data-src', product.image);
      imgbox.appendChild(img);
    }
    card.appendChild(imgbox);

    var body = document.createElement('div');
    body.className = 'ils-ups__body';
    var title = document.createElement('p');
    title.className = 'ils-ups__title';
    title.textContent = product.title;
    body.appendChild(title);

    var wts = document.createElement('div');
    wts.className = 'ils-ups__wts';
    product.variants.forEach(function (v) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'ils-ups__wt';
      chip.setAttribute('data-variant', String(v.id));
      chip.textContent = v.label;
      if (!v.available) chip.setAttribute('data-soldout', 'true');
      else chip.addEventListener('click', function () {
        manualPick[product.handle] = v.id;
        paintCard(card, product);
      });
      wts.appendChild(chip);
    });
    body.appendChild(wts);

    var price = document.createElement('div');
    price.className = 'ils-ups__price';
    body.appendChild(price);

    var add = document.createElement('button');
    add.type = 'button';
    add.className = 'ils-ups__add';
    add.textContent = T.add;
    add.addEventListener('click', function () { addToCart(product, card, add); });
    body.appendChild(add);

    card.appendChild(body);
    return card;
  }

  /* ---- lazy images (TASK-4839) -------------------------------------------
     The carousel is a HORIZONTAL scroller, so the IntersectionObserver's root is
     the scroller itself, not the viewport: a card sitting 1500px to the right is
     off-screen for the shopper even though the block as a whole is in view, and
     a viewport-rooted observer (or native lazy-loading alone, whose distance
     threshold is ~1250px) would fetch most of the row anyway.

     rootMargin gives 200px of lead on both sides, so an image is already in
     flight before its card is fully visible and a swipe lands on a painted card.
     Each image is promoted exactly once and then unobserved; the whole observer
     is rebuilt with the card set, because elScroller.innerHTML = '' has already
     dropped its previous targets.

     No IntersectionObserver (a browser old enough that native lazy-loading is
     absent too) → every image is promoted immediately. The fallback is "behave
     exactly as TASK-4838 did", never "leave a card blank". */
  var imgObserver = null;
  function promoteImg(img) {
    var src = img.getAttribute('data-src');
    if (!src) return;
    img.removeAttribute('data-src');
    img.src = src;
  }
  function observeLazyImages() {
    if (imgObserver) { imgObserver.disconnect(); imgObserver = null; }
    var pending = elScroller.querySelectorAll('.ils-ups__img[data-src]');
    if (!pending.length) return;
    if (!window.IntersectionObserver) {
      for (var i = 0; i < pending.length; i++) promoteImg(pending[i]);
      return;
    }
    imgObserver = new IntersectionObserver(function (entries) {
      for (var k = 0; k < entries.length; k++) {
        if (!entries[k].isIntersecting) continue;
        promoteImg(entries[k].target);
        if (imgObserver) imgObserver.unobserve(entries[k].target);
      }
    }, { root: elScroller, rootMargin: '0px 200px 0px 200px', threshold: 0 });
    for (var j = 0; j < pending.length; j++) imgObserver.observe(pending[j]);
  }

  // Active chip + price for ONE card. Uses the shopper's manual chip if they
  // tapped one for the CURRENT gap, otherwise the heuristic's answer.
  function paintCard(card, product) {
    var pick = null;
    var manual = manualPick[product.handle];
    if (manual) pick = product.variants.filter(function (v) { return v.id === manual && v.available; })[0] || null;
    if (!pick) pick = pickVariant(product, currentMissingCents);
    if (!pick) return;
    card.querySelectorAll('.ils-ups__wt').forEach(function (chip) {
      chip.classList.toggle('is-active', chip.getAttribute('data-variant') === String(pick.id));
    });
    card.querySelector('.ils-ups__price').textContent = eurCents(pick.price);
    card.setAttribute('data-picked', String(pick.id));
  }

  /* =========================================================================
     TASK-4841 — stamp the delivery properties onto a line this page added
     -------------------------------------------------------------------------
     THE GAP. On this store the LINE-ITEM PROPERTIES, not the cart attributes,
     are what survives into the final order (TASK-3223, which is why TASK-3228
     added them). The selector writes them to every line in the cart when the
     selection becomes complete, and its Next handler then calls
     ensurePersisted(selectionSignature()), which resolves IMMEDIATELY while
     writeState.writtenSig still equals that signature. Adding a product does
     not change the SELECTION, so nothing re-runs writeLineItemProperties():
     a line created by this row AFTER the selector has persisted would reach
     checkout carrying no _delivery_* properties at all.

     The test-page prototype could not see this: TASK-4836's guard suppressed
     /cart/change.js outright, so no line on that page ever had the properties.

     THE FIX, WITHOUT INVENTING A VALUE AND WITHOUT TOUCHING THE SELECTOR.
     The correct property set is already in the cart — the selector put it
     there. So: read the properties off a line that HAS them and copy that exact
     object onto every line that does not. Nothing is derived from this page's
     own state, nothing is reconstructed from the cart attributes, and no key
     list is duplicated here; whatever the selector wrote is what gets copied,
     so this cannot drift from lineItemPropsFromAttrs() if that ever changes.

     WHEN IT DOES NOTHING:
       • no line carries _delivery_* yet — the shopper has not completed a
         selection, or the selector's own write has not landed. The selector's
         pass covers every line including the new one, so stamping here would be
         redundant at best and a race at worst.
       • every line already carries them.
     It is also strictly additive: a line that already has properties is never
     rewritten, so a selection made AFTER the add still wins (the selector
     rewrites all lines) and this can never put back a stale value.

     /cart/change.js is the only AJAX endpoint that sets line-item properties,
     and it takes one line at a time, so the pass is serial — Shopify serialises
     cart mutations per token and parallel change.js calls can lock each other
     out (the same reason the selector's own pass is serial). quantity is
     re-sent so nothing but the properties changes. A line's `key` changes once
     its properties change, but each key is taken from one /cart.js snapshot and
     touched exactly once, and the keys of untouched lines are unaffected.
     ========================================================================= */
  var DELIVERY_PROP_RE = /^_delivery_/;

  function deliveryPropsOf(item) {
    var p = item && item.properties;
    if (!p) return null;
    var found = null;
    for (var k in p) {
      if (!Object.prototype.hasOwnProperty.call(p, k)) continue;
      if (!DELIVERY_PROP_RE.test(k)) continue;
      if (!found) found = {};
      found[k] = p[k];
    }
    return found;
  }

  function stampDeliveryProps() {
    return fetch(localeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        if (!items.length) return null;
        var props = null;
        for (var i = 0; i < items.length && !props; i++) props = deliveryPropsOf(items[i]);
        if (!props) return null;              // nothing persisted yet — selector's job
        var missing = items.filter(function (it) { return !deliveryPropsOf(it); });
        if (!missing.length) return null;     // every line already stamped
        var chain = Promise.resolve();
        missing.forEach(function (it) {
          chain = chain.then(function () {
            return fetch(localeRoot() + 'cart/change.js', {
              method: 'POST',
              keepalive: true,
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: props })
            }).then(function (r) {
              if (!r.ok) throw new Error('change.js HTTP ' + r.status + ' for line ' + it.key);
              return r.json();
            });
          });
        });
        return chain.then(function () { return { stamped: missing.length }; });
      })
      // A failure here must never break the add or the delivery flow: the cart
      // attributes are still correct and the shopper can still check out.
      .catch(function (err) {
        try { console.warn('[ils-upsell] could not stamp delivery properties onto the new line:', err); } catch (e) {}
        return null;
      });
  }

  function addToCart(product, card, btn) {
    var id = card.getAttribute('data-picked');
    if (!id) return;
    // TASK-4837 — capture the flight's start point and its thumbnail BEFORE the
    // request. A successful add can rebuild the card row (the product is now in
    // the cart and drops out of the candidate list), so by the time the response
    // lands this card and this button may no longer be in the document.
    var flyFrom = null, flySrc = null;
    try {
      var r = btn.getBoundingClientRect();
      if (r && (r.width || r.height)) flyFrom = { left: r.left, top: r.top, width: r.width, height: r.height };
      var im = card.querySelector('.ils-ups__img');
      if (im) flySrc = im.currentSrc || im.src || null;
    } catch (e) {}
    btn.disabled = true;
    fetch(localeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      // The flight starts the instant the add SUCCEEDS and blocks nothing: the
      // cart re-read, the total repaint and the delivery-price refresh all
      // proceed while it is in the air. ONE flight per click — the button is
      // disabled for the whole round trip, and this is its only call site.
      .then(function (added) {
        flyToCartTotal(flyFrom, flySrc);
        // TASK-4916 — the ONLY call site that adds from the delivery upsell
        // carousel, so the delivery-specific event cannot be produced anywhere
        // else and no ordinary add is mislabelled. Called AFTER the flight has
        // been started, so it cannot delay the confirmation animation; the
        // generic add_to_cart that ils-tracker derives from this very response
        // still fires, untouched.
        trkUpsellAdd(added, id);
        return readCart();
      })
      // TASK-4841 — the new line needs the delivery properties the selector
      // will not re-write. Deliberately AFTER readCart()/render() have the
      // numbers on screen: it is order-data housekeeping, not UI, and must
      // never delay the subtotal, the carousel rebuild or the button.
      .then(function () { btn.disabled = false; render(); return stampDeliveryProps(); })
      .catch(function (err) {
        btn.disabled = false;
        try { console.error('[ils-upsell] add to cart failed:', err); } catch (e) {}
      });
  }

  /* ---- TASK-4916 — funnel telemetry for THIS block ------------------------
     Two events, emitted through the store's EXISTING first-party tracker
     (assets/ils-tracker.js -> POST /api/track/batch -> tracker_events), with
     the existing visitor_id / session_id correlation and the existing session
     UTM / referrer / device columns. No second analytics stack, no new
     endpoint, no new storage key, and nothing at all if the tracker is absent.

       delivery_upsell_impression  — the block was really shown, with cards
       delivery_upsell_add_to_cart — an item was added FROM THIS BLOCK

     The second one is strictly ADDITIVE: the generic add_to_cart that
     ils-tracker derives from the /cart/add.js response still fires for the
     same click, untouched, so the AddToCart funnel number does not move and a
     normal PDP / cart / drawer add can never be mislabelled as a delivery
     upsell (nothing outside this file emits these names).

     Everything here is wrapped so a failure can never reach the shopper: the
     add request, the flying thumbnail, the subtotal repaint and the delivery
     re-price all run exactly as before whether or not the tracker is there. */
  function trk(type, payload) {
    try {
      var TR = window.ILSTracker;
      if (!TR || typeof TR.track !== 'function') return;
      var b = blob() || {};
      var p = payload || {};
      p.cart_token = shopCartToken || (typeof TR.getCartToken === 'function' ? TR.getCartToken() : null);
      p.country = b.country || null;
      p.method = b.method || null;
      p.operator = b.operator || null;
      // {immediate:true} — the plain keepalive fetch, flushed now. NOT the
      // tracker's beacon path, which it has no reason to use: no navigation is
      // in progress when either of these two events fires.
      TR.track(type, p, { immediate: true });
    } catch (e) {}
  }

  // Once per PAGE LOAD, the same unit as the page's own page_view. The row
  // hides and re-shows as the shopper switches country / method, and none of
  // those is a new "the shopper saw the upsell".
  var impressionSent = false;

  // Identifiers come from the /cart/add.js RESPONSE — the authoritative
  // product_id / variant_id / sku / grams / price — not from the card markup.
  function trkUpsellAdd(added, pickedVariantId) {
    try {
      var it = (added && added.items && added.items[0]) || added || {};
      trk('delivery_upsell_add_to_cart', {
        product_id: it.product_id || null,
        variant_id: it.variant_id || (pickedVariantId ? Number(pickedVariantId) : null),
        sku: it.sku || null,
        handle: it.handle || null,
        title: it.product_title || it.title || null,
        qty: it.quantity || 1,
        grams: typeof it.grams === 'number' ? it.grams : null,
        price_cents: typeof it.final_line_price === 'number' ? it.final_line_price
          : (typeof it.price === 'number' ? it.price : null),
        missing_cents: currentMissingCents,
        source: 'delivery_upsell'
      });
    } catch (e) {}
  }

  /* ---- render ------------------------------------------------------------ */
  var currentMissingCents = 0;

  function hide() {
    if (block) block.hidden = true;
    lastSig = 'hidden';
  }

  function render() {
    var complete = !elNext.disabled;
    if (!complete) return hide();

    var b = blob();
    var threshold = selectedThreshold(b);
    // No threshold for the SELECTED method (or the rates are still in flight on
    // the very first paint) → nothing to upsell toward: render nothing.
    if (threshold == null || cartCents == null) return hide();

    var missing = Math.round(Number(threshold) * 100) - cartCents;
    // Selected method is ALREADY free for this cart → render NOTHING. No success
    // message, no placeholder, no empty container.
    if (missing <= 0) return hide();

    currentMissingCents = missing;
    var products = chooseProducts(missing);
    if (!products.length) return hide();

    var sig = [b.country, b.method, b.operator || '', missing, cartToken,
               products.map(function (p) { return p.handle; }).join(',')].join('|');
    if (sig === lastSig) return;
    lastSig = sig;

    if (!block) buildShell();
    block.hidden = false;

    // TASK-4916 — DELIVERY UPSELL IMPRESSION. This is the ONE line that reveals
    // the block, and render() has already returned hide() above for every case
    // in which it must not be shown: incomplete delivery selection, a selected
    // method with no free-shipping threshold, a cart already over it, and an
    // empty product set. So reaching here means the row really is on screen
    // with cards — the block's own current definition of "shown".
    if (!impressionSent) {
      impressionSent = true;
      trk('delivery_upsell_impression', {
        missing_cents: missing,
        threshold_eur: Number(threshold),
        products: products.map(function (p) { return p.id; }),
        handles: products.map(function (p) { return p.handle; }),
        card_count: products.length
      });
    }

    // Line 1 — the dynamic amount still missing, exact to the cent.
    // TASK-4837: same wording, same calculation — only the WEIGHT changed. The
    // sentence renders at normal weight with exactly one bold word; appendCopy
    // splits the very same strings, so no character can be gained or lost.
    elMain.textContent = '';
    emphasisedYet = false;
    appendCopy(elMain, T.main);
    var amt = document.createElement('span');
    amt.className = 'ils-ups__amount';
    amt.textContent = eurCents(missing);
    elMain.appendChild(amt);
    if (T.mainTail) appendCopy(elMain, T.mainTail);

    // Line 2 — smaller, and about the SELECTED method only. No comparison.
    elSub.textContent = (b.method === 'locker' ? T.locker : T.courier) + eurThreshold(threshold);

    var setSig = products.map(function (p) { return p.handle; }).join(',');
    if (setSig !== builtFor) {
      manualPick = {};
      elScroller.innerHTML = '';
      products.forEach(function (p, i) { elScroller.appendChild(cardFor(p, i)); });
      elScroller.scrollLeft = 0;
      observeLazyImages();   // TASK-4839 — arm the lazy images for THIS card set
      builtFor = setSig;
    }
    products.forEach(function (p) {
      var card = elScroller.querySelector('.ils-ups__card[data-handle="' + p.handle + '"]');
      if (card) paintCard(card, p);
    });
  }

  /* ---- change detection -------------------------------------------------- */
  var queued = false;
  var nextFrame = window.requestAnimationFrame
    ? function (fn) { window.requestAnimationFrame(fn); }
    : function (fn) { window.setTimeout(fn, 0); };
  function schedule() {
    if (queued) return;
    queued = true;
    // TASK-4838: syncDeliveryPrices() runs on EVERY tick, before render(), and
    // unconditionally — render() returns early until the selection is complete,
    // but a method/operator row is on screen (and can be stale) well before
    // that, and the selector rebuilds those rows from scratch on every country
    // or method change.
    nextFrame(function () { queued = false; syncDeliveryPrices(); render(); });
  }

  // Re-evaluate on every selector change. Mutations made BY this block are
  // ignored, so rendering can never feed itself.
  function ownNode(n) {
    if (n && n.nodeType === 1 && n.hasAttribute && n.hasAttribute('data-ils-upsell')) return true;
    var el = n && (n.nodeType === 1 ? n : n.parentNode);
    return !!(el && el.closest && el.closest('[data-ils-upsell]'));
  }
  function allOwn(list) {
    for (var i = 0; i < list.length; i++) if (!ownNode(list[i])) return false;
    return true;   // an empty list is trivially all-ours
  }
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var rec = records[i];
      if (ownNode(rec.target)) continue;
      // TASK-4837 — a childList mutation reports the PARENT as its target, so
      // inserting OUR cart-total row into the selector's own footer would read
      // as a selector change. Skip a mutation whose added AND removed nodes are
      // all ours. (A removed node has no parentNode left, hence ownNode's
      // hasAttribute shortcut.)
      if ((rec.addedNodes.length || rec.removedNodes.length) &&
          allOwn(rec.addedNodes) && allOwn(rec.removedNodes)) continue;
      var b = blob();
      if (b && b.country) prefetchRates(b.country);
      schedule();
      return;
    }
  }).observe(root, { subtree: true, childList: true, attributes: true, characterData: true });

  // Capture phase: start the threshold prefetch the same instant the shopper
  // taps a country row — i.e. together with the selector's own /rates call, not
  // one round trip behind it. Matters for courier-only countries, where picking
  // the country auto-selects courier and completes the selection immediately.
  document.addEventListener('click', function (e) {
    var row = e.target && e.target.closest && e.target.closest('[data-iso]');
    if (row) prefetchRates(row.getAttribute('data-iso'));
  }, true);

  window.addEventListener('pageshow', function () { readCart().then(function () { lastSig = null; render(); }); });

  /* ---- init: warm everything BEFORE the selection can complete ------------ */
  (function init() {
    // Warm the product images so a card never paints empty. TASK-4839: only the
    // cards that can be on screen without a swipe are warmed. Warming all 16
    // candidates cost 16 image fetches on every page load — including the loads
    // where the upsell row never appears at all, because the shopper's cart was
    // already over the threshold — and it would have hidden the cost of going
    // from 8 cards to 12. The rest are fetched by observeLazyImages(), as they
    // are swiped toward.
    CANDIDATES.slice(0, HEURISTIC.EAGER_IMAGES).forEach(function (p) {
      if (p.image) { var i = new Image(); i.src = p.image; }
    });

    // Warm the thresholds for the country the selector's own preselection ladder
    // will land on: the saved pick, then the Shopify Market country, then the
    // country half of the locale route — the same rungs, so this is a hit in
    // practice, and any later country tap re-warms anyway.
    var b = blob();
    if (b && b.country) prefetchRates(b.country);
    try { if (window.Shopify && window.Shopify.country) prefetchRates(window.Shopify.country); } catch (e) {}
    var m = localeRoot().match(/^\/[a-z]{2}-([a-z]{2})\/$/i);
    if (m) prefetchRates(m[1]);

    // TASK-4837 — the footer total exists from the first paint, independently
    // of the upsell block (which only appears once a delivery selection is
    // complete). Built before the cart read so it reserves its height once.
    buildCartRow();
    window.addEventListener('resize', function () { lastFootH = null; syncFootHeight(); });

    readCart().then(schedule);
  })();
})();
